import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { T, useTranslation } from "@redotech/redo-i18n";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { memo } from "react";
import { useLocation, useMatches, useNavigate } from "react-router-dom";
import {
  ReturnsExperience,
  useReturnsExperience,
} from "../../../return/experience/use-returns-experience";
import {
  bannerLead,
  ExperienceBannerLink,
  ExperienceBannerShell,
} from "./experience-banner-shell";

/**
 * On the returns, claims, warranties and withdrawals pages, offers the way
 * back from the new returns experience and the way into it. Neither banner
 * can be dismissed, so the switch stays one click away on every page it
 * affects. The pages switch in place: the gates re-read the user's choice.
 * Trying the new experience from a table opens the returns home, where the
 * new experience starts; from a record it stays on that record.
 */
export const ReturnsExperienceBanner = memo(function ReturnsExperienceBanner() {
  const experience = useReturnsExperience();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { pathname } = useLocation();

  const onReturnsPages = [
    routes.returnsHref,
    routes.claimsHref,
    routes.warrantiesHref,
    routes.withdrawalHref,
  ].some((href) => pathname === href || pathname.startsWith(`${href}/`));

  if (!experience.canSwitch || !onReturnsPages) {
    return null;
  }
  return experience.useNewUi ? (
    <SwitchBackBanner experience={experience} />
  ) : (
    <SwitchToNewBanner experience={experience} />
  );
});

const RECORD_ID_PARAMS = ["returnId", "withdrawalId"];

function useOnRecordPage(): boolean {
  const params = useMatches().at(-1)?.params ?? {};
  return RECORD_ID_PARAMS.some((key) => params[key] !== undefined);
}

const SwitchBackBanner = memo(function SwitchBackBanner({
  experience,
}: {
  experience: ReturnsExperience;
}) {
  const { t } = useTranslation();
  return (
    <ExperienceBannerShell testId="returns-experience-switch-back-banner">
      <T
        components={{
          ...emphasis,
          cta: (
            <ExperienceBannerLink
              dataTestId="returns-experience-switch-back"
              dataTrackId="merchant.returns.experience.switch-back.banner-link"
              onClick={experience.switchToLegacy}
              text={t("switch back", {
                context: "link to return to the previous returns pages",
              })}
            />
          ),
        }}
        i18nKey="<lead>You're using the new returns experience.</lead> You can <cta/> to the previous one anytime."
      />
    </ExperienceBannerShell>
  );
});

const SwitchToNewBanner = memo(function SwitchToNewBanner({
  experience,
}: {
  experience: ReturnsExperience;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const navigate = useNavigate();
  const onRecordPage = useOnRecordPage();

  const switchToNew = useHandler(() => {
    experience.switchToNew();
    if (!onRecordPage) {
      navigate(routes.returnsHref);
    }
  });

  return (
    <ExperienceBannerShell
      action={
        <RedoButton
          dataTestId="returns-experience-switch-to-new"
          dataTrackId="merchant.returns.experience.switch-to-new.banner-button"
          hierarchy="secondary"
          onClick={switchToNew}
          size="sm"
          text={t("Try the new experience")}
        />
      }
      testId="returns-experience-switch-to-new-banner"
    >
      <T
        components={emphasis}
        i18nKey="<lead>You're using the previous returns experience.</lead> Try the new one anytime; you can switch back."
      />
    </ExperienceBannerShell>
  );
});

const emphasis = { lead: bannerLead };
